import React from 'react'
import styled from 'styled-components'
import { storyblokEditable, SbBlokData, Richtext } from '@storyblok/react'
import { containerDefault } from '../../lib/styles/Container'
import { ButtonBlok, TeaserCardBlok } from '../../types'
import CustomLink from '../../components/CustomLink'
import { Button } from '../../components/UI/Button'
import { useI18n } from '../../lib/context/i18n'
import { renderRichtext } from '../../lib/utils/renderRichtext'
import { TeaserCard } from '../../components/UI/TeaserCard'
import { TeaserCardItem } from '../TeaserCardItem'
import HeroModuleTag from '../HeroModule/HeroModuleTag'

const Container = styled.div<{ $imagePosition?: 'left' | 'right'; $padding?: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: ${props => props.$padding || '3rem 0'};
  align-items: center;
  direction: ${props => props.$imagePosition === 'left' ? 'rtl' : 'ltr'};

  > * {
    direction: ltr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const Wrapper = styled.div<{ backgroundColor: string; $blockBorderRadius?: string, $margin?: string }>`
    ${containerDefault}
    width: 100%;
    margin: ${props => props.$margin || '0rem auto'};
    border-radius: ${props => props.$blockBorderRadius || '0.5rem'};
    justify-content: center;
    background-color: ${props => props.backgroundColor || 'transparent'};

    @media (max-width: 768px) {
        padding: 0;
    }
`

const TextContent = styled.div`
  padding: 1rem;
`

const Heading = styled.h2<{ $fontSize?: string; $textColor?: string }>`
  color: ${props => props.$textColor || 'var(--Black-black-1, #0D0D0D)'};
  font-size: ${props => props.$fontSize || '2.5rem'};
  font-style: normal;
  font-weight: 700;
  line-height: 115%;
  margin-bottom: 1rem;
`

const Description = styled.p<{ $fontSize?: string; $textColor?: string }>`
  font-size: ${props => props.$fontSize || '1.125rem'};
  color: ${props => props.$textColor || 'inherit'};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`
const ImageWrapper = styled.div<{ $imageWidth?: string }>`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`

const Image = styled.img<{ $imageBorderRadius?: string, $imageWidth?: string }>`
//   width: ${props => props.$imageWidth || '100%'};
  height: auto;
  border-radius: ${props => props.$imageBorderRadius || '0.5rem'};
`


const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`

const TeaserCardWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`

export interface DeveloperModuleContent {
    blok: {
        backgroundColor: string
        heading: string
        description: string
        headingFontSize?: string
        headingColor?: string
        descriptionFontSize?: string
        descriptionColor?: string
        buttons: ButtonBlok[]
        showTag?: boolean
        tagText?: string
        tagColor?: string
        tagBgColor?: string
        tagFontSize?: string
        image: {
            filename: string
            alt: string
        }
        blockBorderRadius?: string
        imageBorderRadius?: string
        padding?: string
        margin?: string
        imageWidth?: string
        imagePosition?: 'left' | 'right'
        richtext?: Richtext
        teaserCards?: TeaserCardBlok[]
    }
}


const DeveloperModuleContent = ({ blok }: DeveloperModuleContent & { blok: SbBlokData }) => {
    const { getTranslatedUrlFromLink } = useI18n()
    return (
        <Wrapper backgroundColor={blok.backgroundColor} $margin={blok.margin}>
            <Container {...storyblokEditable(blok)} $imagePosition={blok.imagePosition} $padding={blok.padding}>
                <TextContent>
                    {blok.showTag && <HeroModuleTag blok={{
                        showTag: blok.showTag,
                        tagText: blok.tagText,
                        tagColor: blok.tagColor,
                        tagBgColor: blok.tagBgColor,
                        tagFontSize: blok.tagFontSize,
                        _uid: 'HeroModuleTag',
                        component: 'HeroModuleTag'
                    }} />}
                    <Heading
                        $fontSize={blok.headingFontSize}
                    >
                        {blok.heading}
                    </Heading>
                    <Description
                        $fontSize={blok.descriptionFontSize}
                        $textColor={blok.descriptionColor}
                    >
                        {blok.description}
                    </Description>
                    <div className="prose">{renderRichtext(blok.richtext as Richtext)}</div>
                    <ButtonContainer>
                        {blok.buttons.length > 0 && blok.buttons?.map((button: ButtonBlok, index: number) => (
                            <Button
                                key={index}
                                as={CustomLink}
                                to={
                                    button.product_code
                                        ? `${getTranslatedUrlFromLink(button.link)}?product_code=${button.product_code}`
                                        : getTranslatedUrlFromLink(button.link)
                                }
                                $variant={button.button_style as 'primary' | 'secondary' | 'red' | 'green' | 'blue' | 'yellow'}
                                $inverted={button.inverted}
                                title={button.link_title}
                                iconRight={button.iconRight}
                                iconLeft={button.iconLeft}
                            >
                                {button.button_name}
                            </Button>
                        ))}
                    </ButtonContainer>
                </TextContent>
                <TeaserCardWrapper>
                    {blok.teaserCards?.map((teaserCard, index) => (
                        <TeaserCardItem
                            direction={'row'}
                            width='100%'
                            key={index}
                            blok={teaserCard}
                        />
                    ))}
                </TeaserCardWrapper>
            </Container>
        </Wrapper>
    )
}

export default DeveloperModuleContent